import { stringify } from 'csv-stringify/browser/esm/sync';
import { pathOr } from 'ramda';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// TODO Move most of this to core and reuse across export in UI and cli/scheduled export

export const useBotConversationHelpers = () => {
  // Default columns to be exported
  const DEFAULT_EXPORT_COLUMNS = [
    { key: 'id', header: 'ID' },
    { key: 'tenantId', header: 'TenantID' },
    { key: 'botId', header: 'BotID' },
    { key: 'botName', header: 'BotName' },
    { key: 'datetime', header: 'Datetime' },
    { key: 'endDatetime', header: 'EndDatetime' },
    { key: 'datetimeUTC', header: 'DatetimeUTC' },
    { key: 'endDatetimeUTC', header: 'EndDatetimeUTC' },
    { key: 'duration', header: 'Duration' },
    { key: 'state.escalated', header: 'Escalated' },
    { key: 'CSat', header: 'CSat' },
    { key: 'AgentName', header: 'AgentName' },
    { key: 'AgentId', header: 'AgentId' },
    { key: 'AgentEmail', header: 'AgentEmail' },
    { key: 'analysis.category', header: 'Category' },
    { key: 'analysis.subcategory', header: 'Subcategory' },
    { key: 'analysis.subsubcategory', header: 'Tertiary' },
    { key: 'clientAttributes.remoteIp', header: 'RemoteIp' },
    { key: 'clientAttributes.referrer', header: 'Referrer' },
    { key: 'channelAttributes.media', header: 'Media' },
    { key: 'state.summary', header: 'Summary' },
    { key: 'state.handoffReason', header: 'Handoff Reason' },
    { key: 'clientAttributes.input', header: 'Input' },
  ];

  function botConversations2CSV(botConversations, exportColumns = DEFAULT_EXPORT_COLUMNS) {
    const botConversationsData = Array.isArray(botConversations) ? botConversations : [];

    // todo pre-calculations - i.e. duration, etc.

    // Transform data to match columns
    const csvData = botConversationsData.map((x) => {
      let row = {};
      exportColumns.forEach((col) => {
        row[col.key] = pathOr('', col.key.split('.'), x);
      });
      return row;
    });

    // convert to CSV
    const csv = stringify(csvData, {
      header: true,
      columns: exportColumns,
    });

    return csv;
  }

  function cleanupMessageContent(messageContent) {
    if (!messageContent || typeof messageContent !== 'string' || messageContent === '' || messageContent === 'null') {
      return '';
    }
    // TODO Additional cleanup here if needed
    let res = messageContent.replaceAll('<br>', '');
    return res;
  }

  // Get text of the conversation between Customer (user) and Agent (bot, or later agent)
  // To be used for analysis, like CSat
  // agentRoleName can be set to 'Bot' to analyze bot conversation specifically
  function getConversationText(messages, agentRoleName = 'Agent', customerRoleName = 'Customer') {
    if (!Array.isArray(messages) || messages.length <= 0) {
      return '';
    }
    let text = '';
    let lastActor = null;
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      // Anything we analyze is represented as between Customer and Agent
      if (['user', 'bot', 'assistant', 'agent'].includes(m.role)) {
        const messageActor = m.role;
        const a = messageActor === 'user' ? customerRoleName : agentRoleName;

        // Ensure that if there are multiple consequent messages from the same actor they are concatenated
        const mc = cleanupMessageContent(m.content);
        if (mc && mc !== '') {
          if (messageActor === lastActor) {
            text += ' ' + mc;
          } else {
            text += lastActor ? '\n' : '';
            text += `${a}: ${mc}`;
            lastActor = messageActor;
          }
        }
      }
    }
    text += '\n';
    return text;
  }

  function botConversations2JSON(botConversations, includeTranscript = false, exportColumns = DEFAULT_EXPORT_COLUMNS) {
    const botConversationsData = Array.isArray(botConversations) ? botConversations : [];

    const docs = [];

    for (let i = 0; i < botConversationsData.length; i++) {
      const conv = botConversationsData[i];
      let doc = {};
      exportColumns.forEach((col) => {
        doc[col.header] = pathOr('', col.key.split('.'), conv);
      });

      // transcript
      if (includeTranscript) {
        doc.transcript = getConversationText(conv.messages, 'Bot', 'Customer');
      }

      docs.push(doc);
    }

    return docs;
  }

  // Pre-process Bot Conversation, adding calculated fields, etc.
  function preProcessBotConversation(botConversation) {
    // figure out "end" datetime as datetime of last user or agent message
    let startTs = botConversation.datetime;
    // todo use actual end time - e.g., for voice or timed out conversations
    let endTs = botConversation.datetime;

    if (Array.isArray(botConversation.messages) && botConversation.messages.length > 0) {
      let firstUserMessage = botConversation.messages.find((x) => x.role === 'user');
      if (firstUserMessage) {
        if (startTs > firstUserMessage.datetime) {
          // Adjust start time if we have mismatch (to be corrected)
          startTs = firstUserMessage.datetime;
          botConversation.datetime = startTs;
        }
      }
      let lIdx = botConversation.messages.length - 1;
      while (lIdx >= 0) {
        const m = botConversation.messages[lIdx];
        if (m?.role === 'user' || m?.role === 'assistant' || m?.role === 'agent') {
          endTs = m.datetime;
          break;
        }
        lIdx--;
      }
    }

    // Date time in UTC
    botConversation.datetimeUTC = dayjs.utc(botConversation.datetime).toISOString();
    botConversation.endDatetime = endTs;
    botConversation.endDatetimeUTC = dayjs.utc(botConversation.endDatetime).toISOString();
    botConversation.duration = Math.floor((botConversation.endDatetime - botConversation.datetime) / 1000);
    return botConversation;
  }

  // Convert bot conversation to Markdown format with Transcripts
  function botConversations2TranscriptsMd(botConversations, dataType) {
    const botConversationsData = Array.isArray(botConversations) ? botConversations : [];

    let md = '';

    const mdExportColumns = [
      //{ key: 'id', header: 'ID' },
      { key: 'datetimeUTC', header: 'Date' },
      //{ key: 'duration', header: 'Duration' },
      { key: 'state.escalated', header: 'Escalated' },
      { key: 'CSat', header: 'CSat' },
      { key: 'AgentName', header: 'AgentName' },
      { key: 'analysis.category', header: 'Category' },
      { key: 'analysis.subcategory', header: 'Subcategory' },
      { key: 'analysis.subsubcategory', header: 'Tertiary' },
      { key: 'channelAttributes.media', header: 'Media' },
      { key: 'state.handoffReason', header: 'Handoff Reason' },
    ];

    for (let i = 0; i < botConversationsData.length; i++) {
      const conv = botConversationsData[i];
      let doc = {};

      let docMd = `# Conversation ID: ${conv.id}\n\n`;

      mdExportColumns.forEach((col) => {
        const val = pathOr(null, col.key.split('.'), conv);
        if (val !== null && val !== '') {
          docMd += `${col.header}: ${val}\n`;
        }
      });

      if (dataType === 'summary') {
        const summary = conv?.state?.summary || null;
        if (summary && summary !== '') {
          docMd += `\n## Summary\n\n${summary}\n\n`;
        }
      } else if (dataType === 'transcript') {
        const transcript = getConversationText(conv.messages, 'Agent', 'Customer');
        if (transcript && transcript !== '') {
          docMd += `\n## Transcript\n\n${transcript}\n\n`;
        }
      }

      md += docMd;
      md += '\n';
    }

    return md;
  }

  return {
    botConversations2JSON,
    botConversations2CSV,
    preProcessBotConversation,
    botConversations2TranscriptsMd,
  };
};
